import { LOG_TYPES } from '../constants/logTypeRegistry';
import { summarizeBackendQuickLogResponse } from './quickLogReviewHelpers';

export const getQuickLogLocalParserName = ({
  longNoteCapture = null,
  routingMode = '',
} = {}) => {
  if (longNoteCapture) {
    return 'natural_capture_long_note_adapter';
  }

  if (routingMode === 'long_note_review') {
    return 'natural_capture_long_note_adapter';
  }

  if (routingMode === 'small_structured_review') {
    return 'extractQuickNoteEntities';
  }

  return null;
};

export const getQuickLogLocalParserUsed = (options = {}) => (
  Boolean(getQuickLogLocalParserName(options))
);

export const buildQuickLogFallbackStructuredEntry = ({
  text = '',
  routingDecision = {},
  transcriptNormalization = {},
  structureDecision = {},
} = {}) => {
  const fallbackCategory = routingDecision.captureState?.category || 'log';
  const fallbackTitle = fallbackCategory === 'log'
    ? 'Review needed'
    : (LOG_TYPES[fallbackCategory]?.displayLabel || fallbackCategory);
  const fallbackTitlePrefix = fallbackCategory === 'log'
    ? 'Quick capture'
    : (LOG_TYPES[fallbackCategory]?.displayLabel || fallbackCategory);

  return {
    category: fallbackCategory,
    title: fallbackTitle,
    titlePrefix: fallbackTitlePrefix,
    body: text,
    note: text,
    text,
    content: text,
    description: text,
    summary: text,
    details: text,
    value: text,
    metadata: {
      captureMode: routingDecision.mode,
      routingReason: routingDecision.reason,
      localConfidence: routingDecision.localConfidence,
      transcriptNormalization,
      structureDecision,
    },
  };
};

export const buildQuickLogCaptureDebugPayload = ({
  routingDecision = {},
  transcriptNormalization = {},
  structureDecision = {},
  longNoteCapture = null,
  shouldUseBackendExtraction = false,
  backendResponseData = null,
  backendEntries = [],
  structuredEntries = [],
  shouldRerouteToImportedNarrativeReview = false,
  routingPreview = null,
  text = '',
  quickNoteCategoryDecision = null,
} = {}) => {
  const normalizedBackendEntries = Array.isArray(backendEntries) ? backendEntries : [];
  const normalizedStructuredEntries = Array.isArray(structuredEntries) ? structuredEntries : [];
  const localParserName = getQuickLogLocalParserName({
    longNoteCapture,
    routingMode: routingDecision.mode,
  });
  const baseMode = routingDecision.baseMode || routingDecision.mode || null;
  const finalMode = routingDecision.finalMode || routingDecision.mode || null;

  return {
    captureMode: routingDecision.mode,
    baseMode,
    finalMode,
    longNotePreserved: Boolean(
      routingDecision.longNotePreserved
      || (baseMode === 'long_note_review' && finalMode === 'long_note_review')
    ),
    inputType: routingDecision.inputType || null,
    routingReason: routingDecision.reason,
    localConfidence: routingDecision.localConfidence,
    transcriptNormalization,
    structureDecision,
    claudeCalled: Boolean(shouldUseBackendExtraction),
    localParserUsed: Boolean(localParserName),
    localParserName,
    aiFallbackUsed: Boolean(shouldUseBackendExtraction),
    fallbackReason: shouldUseBackendExtraction ? routingDecision.reason : 'local_confident',
    enrichmentApplied: Boolean(shouldUseBackendExtraction && backendResponseData?.ok && normalizedBackendEntries.length > 0),
    longNoteDetected: Boolean(longNoteCapture),
    longNoteLocalRowCount: longNoteCapture?.localParsedRowCount || 0,
    longNoteGroupedRowCount: longNoteCapture?.groupedRowCount || 0,
    detectedEntityCount: routingDecision.eventCount || 0,
    backendEntryCount: normalizedBackendEntries.length,
    backendOk: Boolean(backendResponseData?.ok),
    structuredEntryCount: normalizedStructuredEntries.length,
    reroutedToImportedNarrativeReview: shouldRerouteToImportedNarrativeReview,
    routingPreview,
    preview: String(text || '').slice(0, 180),
    backendSummary: summarizeBackendQuickLogResponse(
      backendResponseData || {},
      normalizedBackendEntries,
      null
    ),
    quickNoteCategoryDecision,
  };
};

export const buildQuickLogCaptureErrorPayload = ({
  routingDecision = {},
  fallbackLongNoteCapture = null,
  routingPreview = null,
  text = '',
  transcriptNormalization = {},
  structureDecision = {},
  error = null,
} = {}) => {
  const localParserName = getQuickLogLocalParserName({
    longNoteCapture: fallbackLongNoteCapture,
    routingMode: routingDecision.mode,
  });
  const baseMode = routingDecision.baseMode || routingDecision.mode || null;
  const finalMode = routingDecision.finalMode || routingDecision.mode || null;

  return {
    captureMode: 'ai_smart_capture',
    baseMode,
    finalMode,
    longNotePreserved: Boolean(
      routingDecision.longNotePreserved
      || (baseMode === 'long_note_review' && finalMode === 'long_note_review')
    ),
    inputType: routingDecision.inputType || null,
    routingMode: routingDecision.mode,
    routingReason: routingDecision.reason,
    localConfidence: routingDecision.localConfidence,
    claudeCalled: false,
    localParserUsed: Boolean(localParserName),
    localParserName,
    aiFallbackUsed: Boolean(routingDecision.shouldUseBackendExtraction),
    fallbackReason: routingDecision.shouldUseBackendExtraction ? routingDecision.reason : 'local_confident',
    enrichmentApplied: false,
    longNoteDetected: Boolean(fallbackLongNoteCapture),
    longNoteLocalRowCount: fallbackLongNoteCapture?.localParsedRowCount || 0,
    longNoteGroupedRowCount: fallbackLongNoteCapture?.groupedRowCount || 0,
    detectedEntityCount: routingDecision.eventCount || 0,
    backendEntryCount: 0,
    backendOk: false,
    structuredEntryCount: 0,
    routingPreview,
    preview: String(text || '').slice(0, 180),
    transcriptNormalization,
    structureDecision,
    error: {
      message: error?.message || String(error),
      code: error?.code || null,
    },
  };
};
